import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ChevronRightIcon, ExclamationTriangleIcon, PlusIcon } from '@heroicons/react/24/outline';
import { cn } from '@/lib/utils';
import { getAppSocket } from '@/lib/wesSocket';
import ClientCombobox from '../ClientCombobox';
import ClientQuickPicks from '../ClientQuickPicks';
import VehicleCreateDialog from '../VehicleCreateDialog';
import VehicleCopyButton from '../VehicleCopyButton';
import ClienteNuevoDialog from './ClienteNuevoDialog';
import ContactoRecepcion, { CONTACTO_AUTO, ContactoElegido } from './ContactoRecepcion';
import RenglonesTurno from './RenglonesTurno';
import CuandoTurno from './CuandoTurno';
import ResumenTurno from './ResumenTurno';
import { KmTableroInput } from '../KmTablero';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { hoyLocal } from '../../utils/fechaLocal';
import {
  Renglon,
  Estacion,
  ModoCuando,
  SlotInfo,
  DatosTurno,
  duracionSugerida,
  renglonesDePresupuesto,
  nombreCliente,
  etiquetaVehiculo,
  etiquetaDia,
  fmtDuracion,
  sumarMinutos,
  horaHM,
  validarTurno,
  armarPayloadTurno
} from '../../utils/turnoNuevo';

export interface NuevoTurnoInicial {
  clientId?: string;
  vehicleId?: string;
  budgetId?: string;
  date?: string;
  slot?: string;
  immediate?: boolean;
}

interface Props {
  initial: NuevoTurnoInicial;
}

const TIPOS_RECLAMO = [
  { value: 'trabajo_mal_realizado', label: 'Trabajo mal realizado' },
  { value: 'pieza_defectuosa', label: 'Pieza defectuosa' },
  { value: 'problema_no_detectado', label: 'Problema no detectado' },
  { value: 'danio_durante_servicio', label: 'Daño durante el servicio' },
  { value: 'otro', label: 'Otro' }
];

const Bloque: React.FC<{ n: number; titulo: string; hint?: string; children: React.ReactNode }> = ({ n, titulo, hint, children }) => (
  <section className="rounded-xl border border-border bg-card p-4 sm:p-5">
    <div className="mb-3 flex items-center gap-2.5">
      <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">{n}</span>
      <h2 className="text-[15px] font-semibold">{titulo}</h2>
      {hint && <span className="ml-auto hidden text-xs text-muted-foreground sm:inline">{hint}</span>}
    </div>
    {children}
  </section>
);

const errorDe = (error: any, fallback: string) => {
  const d = error?.response?.data;
  if (d?.error) return d.error;
  if (d?.message) return d.message;
  if (Array.isArray(d?.errors) && d.errors.length) return d.errors.map((e: any) => e.msg).filter(Boolean).join(' · ');
  return error?.message || fallback;
};

/**
 * Alta de turno en una sola pantalla: Quién → Qué → Cuándo, con el resumen
 * siempre a la vista. Manda el mismo payload que el formulario anterior a
 * POST /api/appointments-enhanced; el backend no cambió.
 */
const NuevoTurno: React.FC<Props> = ({ initial }) => {
  const navigate = useNavigate();
  const hoy = hoyLocal();
  const inicialRef = useRef(initial);

  // Catálogo
  const [clients, setClients] = useState<any[]>([]);
  const [servicios, setServicios] = useState<any[]>([]);
  const [productos, setProductos] = useState<any[]>([]);
  const [estaciones, setEstaciones] = useState<Estacion[]>([]);

  // Quién
  const [clientId, setClientId] = useState(initial.clientId || '');
  const [vehicleId, setVehicleId] = useState(initial.vehicleId || '');
  const pendingVehicleRef = useRef<string | null>(initial.vehicleId || null);
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [contacto, setContacto] = useState<ContactoElegido>(CONTACTO_AUTO);
  const [clienteNuevoOpen, setClienteNuevoOpen] = useState(false);
  const [clienteNuevoNombre, setClienteNuevoNombre] = useState('');
  const [vehiculoNuevoOpen, setVehiculoNuevoOpen] = useState(false);

  // Qué
  const [presupuestos, setPresupuestos] = useState<any[]>([]);
  const [presupuesto, setPresupuesto] = useState<any | null>(null);
  const [cargandoPresupuesto, setCargandoPresupuesto] = useState(false);
  const [renglones, setRenglones] = useState<Renglon[]>([]);
  const [duracion, setDuracion] = useState(60);
  const [duracionAuto, setDuracionAuto] = useState(true);

  // Cuándo (nada elegido de entrada, salvo que venga de la URL)
  const slotInicial = String(initial.slot || '').split('-')[0].trim();
  const [modo, setModo] = useState<ModoCuando>(initial.immediate ? 'ahora' : initial.date ? (initial.date === hoy ? 'hoy' : 'otro') : '');
  const [fecha, setFecha] = useState(initial.date && initial.date !== hoy ? initial.date : '');
  const [slot, setSlot] = useState(slotInicial);
  const [slots, setSlots] = useState<SlotInfo[]>([]);
  const [cargandoSlots, setCargandoSlots] = useState(false);
  const [ahora, setAhora] = useState(() => new Date());

  // Más detalles
  const [detallesAbiertos, setDetallesAbiertos] = useState(false);
  const [notas, setNotas] = useState('');
  const [km, setKm] = useState('');
  const [reclamo, setReclamo] = useState<{ es: boolean; sourceId: string | null; sourceCode: string; busqueda: string; resultados: any[]; tipo: string; notas: string }>({
    es: false, sourceId: null, sourceCode: '', busqueda: '', resultados: [], tipo: '', notas: ''
  });

  const [guardando, setGuardando] = useState(false);

  // El reloj de "Ahora" se refresca solo (la pantalla puede quedar abierta un rato)
  useEffect(() => {
    const t = setInterval(() => setAhora(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  const cargarVehiculos = async (cid: string) => {
    setVehiclesLoading(true);
    try {
      const r = await axios.get(`/api/vehicles?clientId=${encodeURIComponent(cid)}`);
      const lista = Array.isArray(r.data) ? r.data : Array.isArray(r.data?.vehicles) ? r.data.vehicles : [];
      setVehicles(lista);
    } catch {
      setVehicles([]);
    } finally {
      setVehiclesLoading(false);
    }
  };

  const cargarPresupuestos = async (cid: string) => {
    try {
      const r = await axios.get(`/api/budgets?clientId=${encodeURIComponent(cid)}&status=approved`);
      const lista = Array.isArray(r.data?.budgets) ? r.data.budgets : Array.isArray(r.data) ? r.data : [];
      // Solo los que todavía no tienen un turno abierto
      setPresupuestos(lista.filter((b: any) => !b.linkedAppointmentActive));
    } catch {
      setPresupuestos([]);
    }
  };

  const aplicarPresupuesto = async (id: string, catalogo?: { servicios: any[]; productos: any[] }) => {
    setCargandoPresupuesto(true);
    try {
      const r = await axios.get(`/api/budgets/${id}`);
      const p = r.data || {};
      const nuevos = renglonesDePresupuesto(p, catalogo || { servicios, productos });
      setPresupuesto(p);
      setRenglones((prev) => [...prev.filter((x) => !x.fromBudget), ...nuevos]);
      if (p.clientId && !clientId) setClientId(p.clientId);
      if (p.vehicleId) {
        pendingVehicleRef.current = p.vehicleId;
        setVehicleId((v) => v || p.vehicleId);
      }
      if (nuevos.length > 0) toast.success(`Se cargaron ${nuevos.length} renglones del presupuesto ${p.budgetNumber || ''}`.trim());
    } catch (error: any) {
      toast.error(errorDe(error, 'No se pudo cargar el presupuesto'));
    } finally {
      setCargandoPresupuesto(false);
    }
  };

  const quitarPresupuesto = () => {
    setPresupuesto(null);
    setRenglones((prev) => prev.filter((x) => !x.fromBudget));
  };

  // Catálogo al abrir; después, lo que venga de la URL
  useEffect(() => {
    let vivo = true;
    (async () => {
      const [c, s, p, e] = await Promise.all([
        axios.get('/api/clients?limit=10000').then((r) => r.data?.clients || []).catch(() => []),
        axios.get('/api/products?type=service&limit=all').then((r) => r.data?.products || []).catch(() => []),
        axios.get('/api/products?type=product&limit=all').then((r) => r.data?.products || []).catch(() => []),
        axios.get('/api/reception/stations').then((r) => (r.data?.stations || []).map((x: any) => ({ key: x.key, name: x.name }))).catch(() => [])
      ]);
      if (!vivo) return;
      setClients(c);
      setServicios(s);
      setProductos(p);
      setEstaciones(e);
      if (inicialRef.current.budgetId) await aplicarPresupuesto(inicialRef.current.budgetId, { servicios: s, productos: p });
    })();
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cambió el cliente: sus vehículos, sus presupuestos, y el contacto vuelve a automático
  useEffect(() => {
    if (!clientId) {
      setVehicles([]);
      setPresupuestos([]);
      setVehicleId('');
      return;
    }
    if (!pendingVehicleRef.current) setVehicleId('');
    cargarVehiculos(clientId);
    cargarPresupuestos(clientId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  useEffect(() => {
    setContacto(CONTACTO_AUTO);
  }, [clientId, vehicleId]);

  // Llegó la lista de vehículos: se aplica el pendiente (patente, presupuesto,
  // URL, alta reciente) o, si el cliente tiene uno solo, ese
  useEffect(() => {
    const pend = pendingVehicleRef.current;
    if (pend && vehicles.some((v) => v.id === pend)) {
      setVehicleId(pend);
      pendingVehicleRef.current = null;
      return;
    }
    if (!pend && !vehicleId && vehicles.length === 1) setVehicleId(vehicles[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicles]);

  useEffect(() => {
    if (!clientId) return;
    const socket = getAppSocket();
    const onVehicles = (payload: any) => {
      if (payload?.clientId && payload.clientId !== clientId) return;
      cargarVehiculos(clientId);
    };
    socket.on('vehicles:update', onVehicles);
    return () => { socket.off('vehicles:update', onVehicles); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  // Duración sugerida mientras no se toque a mano
  useEffect(() => {
    if (duracionAuto) setDuracion(duracionSugerida(renglones));
  }, [renglones, duracionAuto]);

  // Horarios del día elegido, con la duración real
  useEffect(() => {
    const dia = modo === 'hoy' ? hoy : modo === 'otro' ? fecha : '';
    if (!dia) {
      setSlots([]);
      return;
    }
    let cancelado = false;
    setCargandoSlots(true);
    const t = setTimeout(async () => {
      try {
        const dur = Math.min(1440, Math.max(15, Math.round(duracion) || 60));
        const r = await axios.get(`/api/appointments-enhanced/available-slots/${dia}?duration=${dur}&resources=taller`);
        if (cancelado) return;
        const all: SlotInfo[] = Array.isArray(r.data?.slotsAll) && r.data.slotsAll.length > 0
          ? r.data.slotsAll
          : (r.data?.availableSlots || []).map((time: string) => ({ time, nivel: 'libre', ocupados: 0, capacidad: 0 }));
        setSlots(all);
      } catch {
        if (!cancelado) setSlots([]);
      } finally {
        if (!cancelado) setCargandoSlots(false);
      }
    }, 250);
    return () => { cancelado = true; clearTimeout(t); };
  }, [modo, fecha, duracion, hoy]);

  // Búsqueda de la OT original del reclamo
  useEffect(() => {
    if (!reclamo.es || reclamo.sourceId || reclamo.busqueda.trim().length < 2) return;
    let cancelado = false;
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`/api/appointments-enhanced?search=${encodeURIComponent(reclamo.busqueda.trim())}&limit=10`);
        if (!cancelado) setReclamo((x) => ({ ...x, resultados: r.data?.appointments || [] }));
      } catch {
        if (!cancelado) setReclamo((x) => ({ ...x, resultados: [] }));
      }
    }, 250);
    return () => { cancelado = true; clearTimeout(t); };
  }, [reclamo.es, reclamo.sourceId, reclamo.busqueda]);

  const cliente = useMemo(() => clients.find((c) => c.id === clientId) || null, [clients, clientId]);
  const vehiculo = useMemo(() => vehicles.find((v) => v.id === vehicleId) || null, [vehicles, vehicleId]);
  const clienteCombobox = cliente ? {
    id: cliente.id,
    firstName: cliente.firstName || '',
    lastName: cliente.lastName || '',
    companyName: cliente.companyName,
    type: (cliente.companyName ? 'company' : 'individual') as 'company' | 'individual',
    email: cliente.email,
    phone: cliente.phone,
    cuit: cliente.cuit,
    dni: cliente.dni
  } : null;

  const elegirModo = (m: ModoCuando) => {
    setModo(m);
    if (m !== 'otro') setFecha('');
    if (m === 'ahora') setSlot('');
  };

  const crear = async () => {
    const enEsteMomento = new Date();
    const datos: DatosTurno = {
      clientId,
      vehicleId: vehicleId || undefined,
      renglones,
      duracion,
      modo,
      fecha: modo === 'otro' ? fecha : hoy,
      slot,
      ahora: enEsteMomento,
      budgetId: presupuesto?.id || null,
      contacto: contacto.source !== 'auto' && contacto.phone ? { name: contacto.name, phone: contacto.phone } : null,
      notas,
      km,
      reclamo: { es: reclamo.es, sourceId: reclamo.sourceId, tipo: reclamo.tipo, notas: reclamo.notas }
    };
    const error = validarTurno(datos);
    if (error) {
      toast.error(error);
      return;
    }
    setGuardando(true);
    try {
      const res = await axios.post('/api/appointments-enhanced', armarPayloadTurno(datos));
      const apt = res.data?.appointment || res.data || {};
      // El contacto tipeado (o tomado de otro vehículo) queda guardado en este
      // vehículo para la próxima. Best effort: no frena la creación.
      if (vehicleId && (contacto.source === 'custom' || contacto.source === 'other') && contacto.phone) {
        axios.patch(`/api/vehicles/${vehicleId}/contact`, {
          contactName: contacto.custom?.name || contacto.name,
          contactLastName: contacto.custom?.lastName || '',
          contactRole: contacto.custom?.role || '',
          contactPhone: contacto.phone
        }).catch(() => {});
      }
      const codigo = apt.appointmentCode ? ` ${apt.appointmentCode}` : '';
      if (modo === 'ahora') {
        toast.success(`Turno${codigo} creado: el vehículo ya está en el taller`, {
          action: { label: 'Ver en Taller', onClick: () => navigate('/taller') }
        });
      } else {
        toast.success(`Turno${codigo} creado para ${etiquetaDia(datos.fecha, hoy).toLowerCase()} a las ${slot}`);
      }
      navigate('/appointments-enhanced');
    } catch (error: any) {
      toast.error(errorDe(error, 'No se pudo crear el turno'));
    } finally {
      setGuardando(false);
    }
  };

  // Resumen
  const cuandoResumen = (() => {
    if (modo === 'ahora') {
      return { titulo: `Hoy ${horaHM(ahora)}`, sub: `entra ahora · ${fmtDuracion(duracion)} · listo ~${sumarMinutos(horaHM(ahora), duracion)}`, tono: 'ok' as const, badge: 'Entra ahora' };
    }
    if (modo === 'hoy' || modo === 'otro') {
      const dia = modo === 'hoy' ? hoy : fecha;
      if (!dia) return { titulo: '', sub: 'falta la fecha', tono: 'muted' as const, badge: 'Sin fecha' };
      if (!slot) return { titulo: etiquetaDia(dia, hoy), sub: 'falta el horario', tono: 'muted' as const, badge: 'Sin horario' };
      return { titulo: `${etiquetaDia(dia, hoy)} ${slot}`, sub: `${fmtDuracion(duracion)} · termina ~${sumarMinutos(slot, duracion)}`, tono: 'info' as const, badge: 'Confirmado' };
    }
    return { titulo: '', sub: 'ahora, hoy u otro día', tono: 'muted' as const, badge: 'Sin definir' };
  })();

  return (
    <div className="pb-24 lg:pb-0">
      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="flex min-w-0 flex-col gap-4">

          {/* 1. Quién */}
          <Bloque n={1} titulo="Quién" hint="Buscá por nombre, teléfono, CUIT o patente">
            <div className="grid gap-3 md:grid-cols-[1.25fr_1fr]">
              <div className="min-w-0">
                <Label className="mb-1.5 block text-xs font-semibold text-muted-foreground">Cliente</Label>
                <div className="[&_input]:h-9 [&_input]:py-1.5">
                  <ClientCombobox
                    clients={clients.map((c) => ({
                      id: c.id, firstName: c.firstName, lastName: c.lastName, companyName: c.companyName,
                      type: c.companyName ? 'company' : 'individual', email: c.email, phone: c.phone, cuit: c.cuit, dni: c.dni
                    }))}
                    selectedClient={clienteCombobox}
                    onSelect={(c) => {
                      pendingVehicleRef.current = null;
                      setClientId(c ? c.id : '');
                    }}
                    placeholder="Buscar cliente…"
                    getClientName={nombreCliente}
                    onCreateClient={(query) => { setClienteNuevoNombre(query); setClienteNuevoOpen(true); }}
                  />
                </div>
                <div className="flex flex-wrap items-center gap-x-3">
                  <ClientQuickPicks
                    className="contents"
                    onConsumidorFinal={() => { pendingVehicleRef.current = null; setClientId('consumidor-final'); }}
                    onPlateResolved={({ client, vehicle }) => {
                      setClients((prev) => prev.some((c) => c.id === client.id)
                        ? prev
                        : [...prev, { id: client.id, firstName: client.name, lastName: '', companyName: '', email: '', phone: client.phone || '' }]);
                      pendingVehicleRef.current = vehicle?.id || null;
                      if (vehicle?.id) setVehicleId(vehicle.id);
                      setClientId(client.id);
                    }}
                  />
                  <button type="button" onClick={() => { setClienteNuevoNombre(''); setClienteNuevoOpen(true); }} className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
                    + Cliente nuevo
                  </button>
                </div>
              </div>

              <div className="min-w-0">
                <Label className="mb-1.5 block text-xs font-semibold text-muted-foreground">Vehículo</Label>
                <div className="flex items-center gap-2">
                <div className="min-w-0 flex-1">
                <Select
                  key={`veh-${vehicles.map((v) => v.id).join(',')}`}
                  value={vehicleId || ''}
                  onValueChange={(v) => {
                    if (v === '__create__') { setVehiculoNuevoOpen(true); return; }
                    pendingVehicleRef.current = null;
                    setVehicleId(v);
                  }}
                  disabled={!clientId}
                >
                  <SelectTrigger className="h-9" aria-label="Vehículo">
                    <SelectValue placeholder={!clientId ? 'Primero el cliente' : vehiclesLoading ? 'Cargando…' : vehicles.length ? 'Elegir vehículo' : 'Sin vehículos: agregá uno'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__create__">
                      <span className="inline-flex items-center gap-1.5"><PlusIcon className="h-3.5 w-3.5" /> Agregar vehículo…</span>
                    </SelectItem>
                    {vehicles.map((v) => (
                      <SelectItem key={v.id} value={v.id}>{etiquetaVehiculo(v)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                </div>
                {/* Marca y modelo al portapapeles, para pedir precio al proveedor */}
                <VehicleCopyButton vehicle={vehiculo} />
                </div>
                {vehiculo && (vehiculo.mileage || vehiculo.color) && (
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    {[vehiculo.mileage ? `${Number(vehiculo.mileage).toLocaleString('es-AR')} km en la ficha` : '', vehiculo.color].filter(Boolean).join(' · ')}
                  </p>
                )}
              </div>
            </div>
            {clientId && clientId !== 'consumidor-final' && (
              <div className="mt-3 border-t border-border pt-3">
                <ContactoRecepcion client={cliente} vehicle={vehiculo} vehicles={vehicles} value={contacto} onChange={setContacto} />
              </div>
            )}
          </Bloque>

          {/* 2. Qué */}
          <Bloque n={2} titulo="Qué" hint="La duración sale de los servicios y define los horarios">
            <RenglonesTurno
              servicios={servicios}
              productos={productos}
              renglones={renglones}
              onRenglones={setRenglones}
              duracion={duracion}
              duracionAuto={duracionAuto}
              onDuracion={(min, auto) => { setDuracionAuto(auto); setDuracion(min); }}
              estaciones={estaciones}
              presupuestos={presupuestos}
              presupuesto={presupuesto}
              cargandoPresupuesto={cargandoPresupuesto}
              onUsarPresupuesto={(id) => aplicarPresupuesto(id)}
              onQuitarPresupuesto={quitarPresupuesto}
            />
          </Bloque>

          {/* 3. Cuándo */}
          <Bloque n={3} titulo="Cuándo" hint="Los horarios muestran el semáforo del taller">
            <CuandoTurno
              modo={modo}
              onModo={elegirModo}
              fecha={fecha}
              onFecha={(f) => { setFecha(f); setSlot(''); }}
              slot={slot}
              onSlot={setSlot}
              slots={slots}
              cargando={cargandoSlots}
              duracion={duracion}
              hoy={hoy}
              ahora={ahora}
            />
            {/* El auto ya está acá: el km queda a mano pero NO es obligatorio (21/09/2026).
                Si se deja vacío, Taller lo pide al completar el trabajo. */}
            {modo === 'ahora' && vehicleId && (
              <div className="mt-3 max-w-xs">
                <Label htmlFor="nt-km-ahora" className="mb-1.5 block text-xs font-semibold text-muted-foreground">Km del tablero <span className="font-normal">(opcional)</span></Label>
                <KmTableroInput id="nt-km-ahora" value={km} onChange={setKm} kmFicha={vehiculo?.mileage ? Number(vehiculo.mileage) : null} />
                <p className="mt-1 text-xs text-muted-foreground">Si no lo tenés ahora, se pide al completar el trabajo en Taller.</p>
              </div>
            )}
          </Bloque>

          {/* Más detalles: lo que casi nunca se usa, plegado */}
          <section className="rounded-xl border border-dashed border-border px-4 sm:px-5">
            <button type="button" onClick={() => setDetallesAbiertos((v) => !v)} className="flex w-full items-center gap-2 py-3 text-left text-sm font-medium text-muted-foreground hover:text-foreground" aria-expanded={detallesAbiertos}>
              <ChevronRightIcon className={cn('h-4 w-4 transition-transform', detallesAbiertos && 'rotate-90')} />
              {modo === 'ahora' && vehicleId ? 'Más detalles: nota para el taller, reclamo' : 'Más detalles: nota para el taller, kilometraje, reclamo'}
              {(notas.trim() || (km && !(modo === 'ahora' && vehicleId)) || reclamo.es) && <span className="ml-auto rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">con datos</span>}
            </button>
            {detallesAbiertos && (
              <div className="grid gap-3 pb-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Label htmlFor="nt-notas" className="mb-1.5 block text-xs font-semibold text-muted-foreground">Nota para el taller</Label>
                  <Textarea id="nt-notas" value={notas} onChange={(e) => setNotas(e.target.value)} rows={2} placeholder="Ej: el cliente pide revisar el ruido al frenar. Retira a las 18." />
                </div>
                {!(modo === 'ahora' && vehicleId) && (
                  <div>
                    <Label htmlFor="nt-km" className="mb-1.5 block text-xs font-semibold text-muted-foreground">Kilometraje</Label>
                    <Input id="nt-km" inputMode="numeric" className="h-9" value={km} onChange={(e) => setKm(e.target.value.replace(/[^\d.]/g, ''))} placeholder={vehiculo?.mileage ? `ficha: ${Number(vehiculo.mileage).toLocaleString('es-AR')}` : 'km actuales'} />
                  </div>
                )}
                <div className={cn('rounded-lg border p-3 sm:col-span-2', reclamo.es ? 'border-destructive/40 bg-destructive/5' : 'border-border')}>
                  <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
                    <Checkbox checked={reclamo.es} onCheckedChange={(v) => setReclamo((x) => ({ ...x, es: Boolean(v), ...(v ? {} : { sourceId: null, sourceCode: '', busqueda: '', resultados: [], tipo: '', notas: '' }) }))} />
                    <ExclamationTriangleIcon className="h-4 w-4 text-destructive" /> Es un reclamo sobre un trabajo anterior
                  </label>
                  {reclamo.es && (
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      <div className="relative">
                        <Label className="mb-1.5 block text-xs font-semibold text-muted-foreground">OT original (opcional)</Label>
                        {reclamo.sourceId ? (
                          <div className="flex h-9 items-center gap-2 text-sm">
                            <span className="font-mono font-semibold text-destructive">{reclamo.sourceCode}</span>
                            <button type="button" onClick={() => setReclamo((x) => ({ ...x, sourceId: null, sourceCode: '', busqueda: '' }))} className="text-xs text-muted-foreground underline underline-offset-2">cambiar</button>
                          </div>
                        ) : (
                          <>
                            <Input className="h-9" value={reclamo.busqueda} onChange={(e) => setReclamo((x) => ({ ...x, busqueda: e.target.value }))} placeholder="Buscar por código OT o cliente…" />
                            {reclamo.resultados.length > 0 && reclamo.busqueda.trim().length >= 2 && (
                              <div className="absolute z-40 mt-1 max-h-[160px] w-full overflow-y-auto rounded-md border border-border bg-popover shadow-lg">
                                {reclamo.resultados.map((a: any) => (
                                  <button key={a.id} type="button" onClick={() => setReclamo((x) => ({ ...x, sourceId: a.id, sourceCode: a.appointmentCode || '', resultados: [] }))} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent">
                                    <span className="font-mono font-semibold">{a.appointmentCode}</span>
                                    <span className="truncate text-muted-foreground">{a.clientName || a.serviceType}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                      <div>
                        <Label className="mb-1.5 block text-xs font-semibold text-muted-foreground">Tipo de reclamo</Label>
                        <Select value={reclamo.tipo} onValueChange={(v) => setReclamo((x) => ({ ...x, tipo: v }))}>
                          <SelectTrigger className="h-9" aria-label="Tipo de reclamo"><SelectValue placeholder="Elegir…" /></SelectTrigger>
                          <SelectContent>
                            {TIPOS_RECLAMO.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="sm:col-span-2">
                        <Label htmlFor="nt-reclamo" className="mb-1.5 block text-xs font-semibold text-muted-foreground">Qué reporta el cliente</Label>
                        <Textarea id="nt-reclamo" rows={2} value={reclamo.notas} onChange={(e) => setReclamo((x) => ({ ...x, notas: e.target.value }))} placeholder="Describí el problema…" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </section>
        </div>

        <ResumenTurno
          cliente={cliente ? { titulo: nombreCliente(cliente), sub: [cliente.phone || cliente.whatsapp, contacto.source !== 'auto' && contacto.name ? `avisos a ${contacto.name}` : ''].filter(Boolean).join(' · ') } : null}
          vehiculo={vehiculo ? { titulo: vehiculo.licensePlate || 'Sin patente', sub: [vehiculo.make, vehiculo.model, vehiculo.year].filter(Boolean).join(' ') } : null}
          cuando={cuandoResumen}
          presupuesto={presupuesto ? `${presupuesto.budgetNumber || 'Presupuesto'} · queda enlazado` : null}
          renglones={renglones}
          duracion={duracion}
          guardando={guardando}
          ctaLabel={modo === 'ahora' ? 'Crear turno y mandar al taller' : 'Crear turno'}
          nota={modo === 'ahora' ? 'Aparece en Taller al instante.' : modo ? 'Confirmación por WhatsApp al contacto elegido.' : ''}
          onCrear={crear}
          onCancelar={() => navigate('/appointments-enhanced')}
        />
      </div>

      <ClienteNuevoDialog
        open={clienteNuevoOpen}
        onOpenChange={setClienteNuevoOpen}
        initialName={clienteNuevoNombre}
        onCreated={(c) => {
          if (!c?.id) return;
          setClients((prev) => prev.some((x) => x.id === c.id) ? prev : [...prev, c]);
          pendingVehicleRef.current = null;
          setClientId(c.id);
        }}
      />
      <VehicleCreateDialog
        open={vehiculoNuevoOpen}
        onOpenChange={setVehiculoNuevoOpen}
        clientId={clientId || null}
        onCreated={async (v: any) => {
          if (v?.id) {
            pendingVehicleRef.current = v.id;
            setVehicleId(v.id);
          }
          if (clientId) await cargarVehiculos(clientId);
        }}
      />
    </div>
  );
};

export default NuevoTurno;
